import { useEffect, useRef, useState, useMemo } from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

import { updateTicket, deleteTicket } from '../api';
import { getModules } from '../api/modules';
import { getAssets } from '../api/assets';

import { StackableModal, AssetSelectorBeta } from '../components/nextgen';
import BetaAttributes from '../components/Attributes';
import { classNames } from '../components/helpers/functions';

import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'editTicket';

const SEVERITY_OPTIONS = [
	{ label: 'Critical', value: 'critical' },
	{ label: 'Major', value: 'major' },
	{ label: 'Minor', value: 'minor' },
];

const EditTicketModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const titleInputRef = useRef(null);

	const [form, setForm] = useState({
		subject: '',
		description: '',
		severity: 'minor',
		categoryId: null,
		locationId: null,
		assetId: null,
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const { value: modules, execute: onRefreshModules } = useAsync(getModules, {
		immediate: false,
	});

	const { value: assetsData, execute: onRefreshAssets } = useAsync(getAssets, {
		immediate: false,
	});

	const ticketsModule = modules?.rows?.find((m) => m.key === 'default:ticket');

	// Process assets data for selector
	const assets = useMemo(() => {
		if (!assetsData?.rows) return [];
		return assetsData.rows.map((asset) => ({
			id: asset.id,
			name:
				[
					asset.manufacturer?.name || asset.manufacturerName,
					asset.model?.name || asset.modelName,
				]
					.filter(Boolean)
					.join(' ') || 'Unnamed Asset',
			manufacturer:
				asset.manufacturer?.name || asset.manufacturerName || 'Unknown',
			model: asset.model?.name || asset.modelName || '',
			type: asset.model?.name || asset.modelName || 'Unknown Type',
			location: asset.location?.name || asset.locationName || 'No Location',
			serial: asset.serialNumber || 'N/A',
		}));
	}, [assetsData]);

	useEffect(() => {
		if (open?.ticket) {
			onRefreshModules();
			onRefreshAssets({ pageSize: null });
			const ticket = open.ticket;
			setForm({
				subject: ticket.subject || '',
				description: ticket.description || '',
				severity: ticket.severity || 'minor',
				categoryId: ticket.category?.id || ticket.categoryId || null,
				locationId: ticket.location?.id || ticket.locationId || null,
				assetId: ticket.asset?.id || ticket.assetId || null,
			});
			setShowDeleteConfirm(false);
		}
	}, [open]);

	const handleClose = () => {
		setForm({
			subject: '',
			description: '',
			severity: 'minor',
			categoryId: null,
			locationId: null,
			assetId: null,
		});
		setShowDeleteConfirm(false);
		setClose();
		if (typeof open?.onClose === 'function') {
			open.onClose();
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!form.subject.trim()) return;

		setIsSubmitting(true);

		try {
			const payload = {
				id: open.ticket.id,
				subject: form.subject,
				description: form.description,
				severity: form.severity,
				categoryId: form.categoryId,
				locationId: form.locationId,
				assetId: form.assetId,
			};

			await updateTicket(payload);

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to update ticket:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDelete = async () => {
		setIsSubmitting(true);
		try {
			await deleteTicket({ id: open.ticket.id });
			if (typeof open?.onDeleted === 'function') {
				open.onDeleted();
			} else if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to delete ticket:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title="Edit Ticket"
			size="xl"
			initialFocusRef={titleInputRef}
		>
			<div className="py-4">
				<form onSubmit={handleSubmit}>
					<div className="space-y-4">
						{/* Title */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Title <span className="text-red-500">*</span>
							</label>
							<input
								ref={titleInputRef}
								type="text"
								value={form.subject}
								onChange={(e) => setForm({ ...form, subject: e.target.value })}
								placeholder="Brief summary of the issue"
								maxLength={100}
								className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
							<p className="mt-1 text-xs text-gray-500 text-right">
								{form.subject.length}/100
							</p>
						</div>

						{/* Description */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Description
							</label>
							<textarea
								value={form.description}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
								placeholder="What's broken? Where is it? Any details that help..."
								rows={3}
								className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
							/>
						</div>

						{/* Priority 
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Priority
							</label>
							<Listbox
								value={form.severity}
								onChange={(val) => setForm({ ...form, severity: val })}
							>
								<div className="relative">
									<Listbox.Button
										type="button"
										className="relative w-full cursor-pointer rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:text-sm"
									>
										<span>
											{SEVERITY_OPTIONS.find((s) => s.value === form.severity)
												?.label || 'Select priority'}
										</span>
										<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
											<ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
										</span>
									</Listbox.Button>
									<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
										{SEVERITY_OPTIONS.map((option) => (
											<Listbox.Option
												key={option.value}
												value={option.value}
												className={({ active }) =>
													classNames(
														active ? 'bg-indigo-50' : '',
														'relative cursor-pointer select-none py-2 pl-3 pr-9',
													)
												}
											>
												{({ selected }) => (
													<>
														<span>{option.label}</span>
														{selected && (
															<span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
																<CheckIcon className="h-5 w-5" />
															</span>
														)}
													</>
												)}
											</Listbox.Option>
										))}
									</Listbox.Options>
								</div>
							</Listbox>
						</div>*/}

						{/* Category 
						<div>
							<BetaAttributes
								field={{
									label: 'Category',
									type: 'category',
									key: 'categoryId',
									moduleId: ticketsModule?.id,
									options: { module_key: 'default:ticket' },
								}}
								form={{
									categoryId: { value: form.categoryId },
								}}
								onChange={(index, value) => {
									setForm({ ...form, categoryId: value?.value || value });
								}}
							/>
						</div>*/}

						{/* Location 
						<div>
							<BetaAttributes
								field={{
									label: 'Location',
									type: 'location',
									key: 'locationId',
								}}
								form={{
									locationId: { value: form.locationId },
								}}
								onChange={(index, value) => {
									setForm({ ...form, locationId: value });
								}}
							/>
						</div>*/}

						{/* Related Asset */}
						<div>
							<label className="block text-sm font-medium text-gray-700 mb-1">
								Related Asset
							</label>
							<AssetSelectorBeta
								value={form.assetId}
								onChange={(assetId) => setForm({ ...form, assetId })}
								assets={assets}
								placeholder="Select an asset..."
								multiple={false}
								allowGroups={false}
							/>
						</div>

						{/* Delete Confirmation */}
						{showDeleteConfirm && (
							<div className="bg-red-50 border border-red-200 rounded-lg p-4">
								<p className="text-sm text-red-700 mb-3">
									Are you sure you want to delete this ticket? This will remove
									all history and cannot be undone.
								</p>
								<div className="flex gap-2 mt-3">
									<button
										type="button"
										onClick={handleDelete}
										disabled={isSubmitting}
										className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
									>
										{isSubmitting ? 'Deleting...' : 'Delete'}
									</button>
									<button
										type="button"
										onClick={() => setShowDeleteConfirm(false)}
										className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
									>
										Cancel
									</button>
								</div>
							</div>
						)}
					</div>

					{/* Actions */}
					<div className="flex justify-between gap-3 pt-6  mt-6">
						<div>
							{!showDeleteConfirm && (
								<button
									type="button"
									onClick={() => setShowDeleteConfirm(true)}
									className="text-sm text-red-600 hover:text-red-700"
								>
									Delete Ticket
								</button>
							)}
						</div>
						<div className="flex gap-3">
							<button
								type="button"
								onClick={handleClose}
								className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
							>
								Cancel
							</button>
							<button
								type="submit"
								disabled={!form.subject.trim() || isSubmitting}
								className={classNames(
									'inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm',
									!form.subject.trim() || isSubmitting
										? 'bg-gray-400 cursor-not-allowed'
										: 'bg-indigo-600 hover:bg-indigo-700',
								)}
							>
								{isSubmitting ? 'Saving...' : 'Save Changes'}
							</button>
						</div>
					</div>
				</form>
			</div>
		</StackableModal>
	);
};

export default EditTicketModal;
